"use client";

import { useEffect, useState, useRef, useMemo } from "react";
import Link from "next/link";
import { api } from "~/trpc/react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { absoluteUrl } from "~/lib/utils";
import { format } from "date-fns/format";
import { srLatn } from "date-fns/locale";
import { parseISO } from "date-fns";
import { usePaymentIntent } from "~/hooks/usePaymentIntent";
import { StripePaymentElements, PaymentErrorState } from "~/components/payment";
import { TherapyRelationshipType } from "~/prisma/browser";
import { callServerActionSafely } from "~/lib/client/call-server-action-safely";
import { getClientTrackingDebugCode } from "~/lib/client/test-diagnostics";
import { pushGA4Event } from "~/lib/ga4-browser";
import { trackEventAction } from "~/server/actions/tracking-actions";
import { useDiscount } from "~/hooks/use-discount";
import { EUR } from "~/lib/currency";
import { ReservationCard } from "~/components/session-booking/reservation-card";
import { Button } from "~/components/ui/button";
import { Skeleton } from "~/components/ui/skeleton";
import { Building2, CheckCircle } from "lucide-react";
import { useOrganisation } from "~/hooks/use-organisation";
import { shouldTrackClientEventOnce } from "~/lib/client/tracking-once";
import {
  buildPaymentReturnUrl,
  cleanPaymentParamsFromUrl,
} from "~/lib/payment/url";

interface SessionDetails {
  therapyType: TherapyRelationshipType;
  therapistName: string;
  therapistImage?: string;
  clientName: string;
  sessionDate: string;
  sessionTime: string;
  sessionCost: number;
  sessionDurationMinutes?: number;
  paymentInfo: {
    therapistId: string;
    sessionTimestamp: string | undefined;
    attributionBridgeId?: string;
    proposalMessageId?: number;
  };
}

type PaymentConfirmationLoadingShellProps = Pick<
  SessionDetails,
  "therapyType" | "therapistName" | "therapistImage" | "sessionDurationMinutes"
> & {
  formattedDateTime: string;
};

export function PaymentConfirmationLoadingShell({
  therapyType,
  therapistName,
  therapistImage,
  formattedDateTime,
  sessionDurationMinutes,
}: PaymentConfirmationLoadingShellProps) {
  return (
    <div
      className="w-full pb-4 md:py-8"
      data-testid="payment-confirmation-loading-shell"
    >
      <div className="mx-auto w-full max-w-2xl">
        <h1 className="font-sans text-2xl font-medium tracking-tight text-foreground md:text-3xl">
          Potvrdi i plati
        </h1>
        <section className="mt-6" aria-label="Detalji rezervacije">
          <ReservationCard
            sessionDetails={{
              therapistName,
              therapistImage,
              therapyType,
              formattedDateTime,
            }}
            sessionDurationMinutes={sessionDurationMinutes}
          />
        </section>

        <section className="mt-6" aria-labelledby="payment-method-loading">
          <div className="flex items-start justify-between gap-4 border-b pb-4">
            <div>
              <h2
                id="payment-method-loading"
                className="text-lg font-semibold text-foreground"
              >
                Način plaćanja
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Pripremamo podatke za plaćanje.
              </p>
            </div>
            <div className="shrink-0 text-right">
              <p className="text-xs text-muted-foreground">Ukupno</p>
              <Skeleton className="ml-auto mt-1 h-7 w-16" />
            </div>
          </div>
          <div
            className="mt-5 space-y-3"
            role="status"
            aria-label="Učitavanje plaćanja"
          >
            <Skeleton className="h-14 w-full" />
            <div className="grid grid-cols-2 gap-3">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
            <span className="sr-only">Učitavanje plaćanja...</span>
          </div>
        </section>
      </div>
    </div>
  );
}

export function PaymentConfirmationComponent(sessionDetails: SessionDetails) {
  const searchParams = useSearchParams();
  const sessionQuantity = 1;
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const pathname = usePathname();
  const router = useRouter();
  const { calculateDiscountedPrice } = useDiscount();
  const utils = api.useUtils();
  const {
    createPaymentIntent,
    clientSecret,
    paymentMetadata,
    paymentSummary,
    error: paymentIntentError,
    isValidatingDiscount,
  } = usePaymentIntent();
  const confirmCreditOnlyBooking =
    api.paymentIntent.confirmCreditOnlyBooking.useMutation();

  // B2B organisation booking
  const { isOrgSponsored, organisationName } = useOrganisation();
  const bookFromOrganisation =
    api.organisation.bookFromOrganisation.useMutation();

  const hasInitialized = useRef(false);

  // If sessionTimestamp is missing or invalid, redirect to step 0
  useEffect(() => {
    const sessionTimestamp = sessionDetails.paymentInfo.sessionTimestamp;
    if (!sessionTimestamp || isNaN(Date.parse(sessionTimestamp))) {
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.set("step", "0");
      router.push(currentUrl.pathname + currentUrl.search);
    }
  }, [sessionDetails.paymentInfo.sessionTimestamp, router]);

  // Handle redirect_status from payment provider and clean up params
  useEffect(() => {
    const redirectStatus = searchParams?.get("redirect_status");

    if (redirectStatus === "succeeded") {
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.set("step", "3");
      cleanPaymentParamsFromUrl(currentUrl);
      router.push(currentUrl.pathname + currentUrl.search);
    } else if (redirectStatus === "failed") {
      // Payment failed - show error on current page
      setPaymentError(
        "Plaćanje nije uspelo. Proveri podatke i pokušaj ponovo.",
      );
      pushGA4Event("payment_failed", {
        currency: "EUR",
        value: sessionDetails.sessionCost,
        items: [
          {
            item_id: sessionDetails.paymentInfo.therapistId,
            item_name: sessionDetails.therapistName,
            price: sessionDetails.sessionCost,
            quantity: 1,
          },
        ],
      });
      void callServerActionSafely(
        "track_payment_failed",
        () =>
          trackEventAction(
            {
              event: "payment_failed",
              data: {
                value: sessionDetails.sessionCost,
                therapistId: sessionDetails.paymentInfo.therapistId,
                therapistName: sessionDetails.therapistName,
                therapyType: sessionDetails.therapyType,
                sourceUrl: window.location.pathname,
              },
            },
            {
              attributionBridgeId:
                sessionDetails.paymentInfo.attributionBridgeId,
              debugTestCode: getClientTrackingDebugCode(),
            },
          ),
        {
          therapistId: sessionDetails.paymentInfo.therapistId,
          sourceUrl: window.location.pathname,
        },
        { failureReporting: "warning" },
      );
      cleanPaymentParamsFromUrl();
    }
  }, [searchParams, router]);

  const sendLoopsEvent = api.marketing.sendLoopsEvent.useMutation();

  // Build clean returnUrl with only necessary params (no payment params)
  const returnUrl = useMemo(() => {
    if (typeof window === "undefined") return "";
    return buildPaymentReturnUrl({
      pathname,
      sessionTimestamp: sessionDetails.paymentInfo.sessionTimestamp,
      therapyType: sessionDetails.therapyType,
      step: 3,
      conversionValue: sessionDetails.sessionCost,
      trackingEventName: paymentMetadata?.trackingEventName,
      proposalMessageId: sessionDetails.paymentInfo.proposalMessageId,
    });
  }, [
    pathname,
    sessionDetails.paymentInfo.sessionTimestamp,
    sessionDetails.paymentInfo.proposalMessageId,
    sessionDetails.therapyType,
    sessionDetails.sessionCost,
    paymentMetadata?.trackingEventName,
  ]);

  useEffect(() => {
    if (isValidatingDiscount || hasInitialized.current) return;

    hasInitialized.current = true;

    createPaymentIntent({
      therapistId: sessionDetails.paymentInfo.therapistId,
      therapyType: sessionDetails.therapyType,
      sessionTimestamp: sessionDetails.paymentInfo.sessionTimestamp,
      attributionBridgeId: sessionDetails.paymentInfo.attributionBridgeId,
      sessionQuantity,
      ...(sessionDetails.paymentInfo.proposalMessageId != null
        ? { proposalMessageId: sessionDetails.paymentInfo.proposalMessageId }
        : {}),
    }).catch((err) => {
      console.error("Failed to create payment intent:", err);
      hasInitialized.current = false;
    });
  }, [
    createPaymentIntent,
    isValidatingDiscount,
    sessionDetails.paymentInfo.sessionTimestamp,
    sessionDetails.paymentInfo.therapistId,
    sessionDetails.paymentInfo.attributionBridgeId,
    sessionDetails.paymentInfo.proposalMessageId,
    sessionDetails.therapyType,
    sessionQuantity,
  ]);

  useEffect(() => {
    const sessionTimestamp = sessionDetails.paymentInfo.sessionTimestamp;

    // Guard clause - if no valid timestamp, don't send the event
    if (!sessionTimestamp) {
      return;
    }

    const trackingKey = [
      "initiate_checkout",
      window.location.pathname,
      sessionDetails.paymentInfo.therapistId,
      sessionTimestamp,
    ].join(":");

    if (!shouldTrackClientEventOnce(trackingKey)) {
      return;
    }

    pushGA4Event("begin_checkout", {
      currency: "EUR",
      value: sessionDetails.sessionCost,
      items: [
        {
          item_id: sessionDetails.paymentInfo.therapistId,
          item_name: sessionDetails.therapistName,
          price: sessionDetails.sessionCost,
          quantity: 1,
        },
      ],
    });

    sendLoopsEvent.mutate({
      eventName: "checkout_started",
      properties: {
        abandonedCheckoutUrl: `${absoluteUrl(pathname)}?step=2&date=${sessionTimestamp}`,
        abandonedCheckoutSessionTime: format(
          parseISO(sessionTimestamp),
          "PPp",
          { locale: srLatn },
        ),
        abandonedCheckoutSessionTherapistName: sessionDetails.therapistName,
        abandonedCheckoutSessionTherapistBookingUrl: absoluteUrl(pathname),
      },
    });
  }, []);

  const sessionTimestamp = sessionDetails.paymentInfo.sessionTimestamp;
  const formattedDateTime = sessionTimestamp
    ? format(parseISO(sessionTimestamp), "PPp", {
        locale: srLatn,
      })
    : `${sessionDetails.sessionDate}, ${sessionDetails.sessionTime}`;

  const basePricePerSession = sessionDetails.sessionCost;
  const price = calculateDiscountedPrice(basePricePerSession);
  const isCreditOnly = paymentSummary?.amountToCharge === 0;
  const payableAmount = paymentSummary?.amountToCharge ?? price;
  const creditApplied = paymentSummary
    ? Math.max(price - paymentSummary.amountToCharge, 0)
    : 0;
  const hasPromoDiscount = price < basePricePerSession;
  const reservationDetails = {
    therapistName: sessionDetails.therapistName,
    therapistImage: sessionDetails.therapistImage,
    therapyType: sessionDetails.therapyType,
    formattedDateTime,
  };
  const paymentHeading = isOrgSponsored
    ? "Rezervacija preko kompanije"
    : isCreditOnly
      ? "Potvrdi rezervaciju"
      : "Način plaćanja";
  const paymentDescription = isOrgSponsored
    ? `Sesiju pokriva ${organisationName}.`
    : isCreditOnly
      ? "Tvoji krediti pokrivaju ceo iznos."
      : "Plati karticom ili preko PayPal-a. Nakon potvrde uplate zakazujemo termin.";

  return (
    <div data-testid="payment-confirmation" className="w-full pb-4 md:py-8">
      <div className="mx-auto w-full max-w-2xl">
        <h1
          className="font-sans text-2xl font-medium tracking-tight text-foreground md:text-3xl"
          data-testid="payment-title"
        >
          Potvrdi i plati
        </h1>
        <section className="mt-6" aria-label="Detalji rezervacije">
          <ReservationCard
            sessionDetails={reservationDetails}
            sessionDurationMinutes={sessionDetails.sessionDurationMinutes}
          />
        </section>

        <section className="mt-6" aria-labelledby="payment-method">
          <div className="flex items-start justify-between gap-4 border-b pb-4">
            <div>
              <h2
                id="payment-method"
                className="text-lg font-semibold text-foreground"
              >
                {paymentHeading}
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                {paymentDescription}
              </p>
            </div>
            {!isOrgSponsored && (
              <div className="shrink-0 text-right">
                <p className="text-xs text-muted-foreground">
                  {sessionQuantity === 1
                    ? "Ukupno"
                    : `${sessionQuantity} sesija`}
                </p>
                <p className="text-xl font-bold text-foreground">
                  {EUR.format(payableAmount)}
                </p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Nema dodatnih troškova
                </p>
              </div>
            )}
          </div>

          {!isOrgSponsored && (hasPromoDiscount || creditApplied > 0) && (
            <div className="mt-4 space-y-1 text-sm text-muted-foreground">
              {hasPromoDiscount && (
                <div className="flex justify-between gap-4">
                  <span>Cena sesije</span>
                  <span className="line-through">
                    {EUR.format(basePricePerSession)}
                  </span>
                </div>
              )}
              {creditApplied > 0 && (
                <div className="flex justify-between gap-4">
                  <span>Dostupan kredit</span>
                  <span>-{EUR.format(creditApplied)}</span>
                </div>
              )}
              {paymentSummary && creditApplied > 0 && (
                <p className="pt-1 text-xs">
                  Preostali kredit nakon kupovine:{" "}
                  {EUR.format(paymentSummary.creditBalance)}
                </p>
              )}
            </div>
          )}

          <div className="mt-5">
            {isOrgSponsored ? (
              // B2B Organisation Booking - No payment required
              <div className="space-y-6">
                {paymentError && (
                  <PaymentErrorState errorMessage={paymentError} />
                )}
                <div className="rounded-xl border-2 border-brand/20 bg-brand/5 p-6 text-center">
                  <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-brand/10">
                    <Building2 className="h-6 w-6 text-brand" />
                  </div>
                  <h4 className="mb-2 text-lg font-semibold text-foreground">
                    Sponzorisano od strane {organisationName}
                  </h4>
                  <p className="mb-2 text-sm text-muted-foreground">
                    Tvoja sesija je besplatna jer si član kompanije.
                  </p>

                  <Button
                    size="lg"
                    onClick={async () => {
                      try {
                        await bookFromOrganisation.mutateAsync({
                          therapistId: sessionDetails.paymentInfo.therapistId,
                          sessionTimestamp:
                            sessionDetails.paymentInfo.sessionTimestamp!,
                        });
                        utils.organisation.getMyOrganisation.invalidate();
                        utils.organisation.canBookFromPool.invalidate();
                        const currentUrl = new URL(window.location.href);
                        currentUrl.searchParams.set("step", "3");
                        router.push(currentUrl.pathname + currentUrl.search);
                      } catch (err) {
                        const errorMessage =
                          err instanceof Error
                            ? err.message
                            : "Neuspešno zakazivanje sesije";
                        setPaymentError(errorMessage);
                      }
                    }}
                    isLoading={bookFromOrganisation.isPending}
                    icon={CheckCircle}
                  >
                    Potvrdi rezervaciju
                  </Button>
                </div>
              </div>
            ) : paymentIntentError ? (
              <PaymentErrorState
                title="Plaćanje trenutno nije moguće"
                errorMessage={paymentIntentError}
                helperText="Izaberi drugi termin i nastavi zakazivanje."
                actionLabel="Izaberi drugi termin"
                onAction={() => {
                  const currentUrl = new URL(window.location.href);
                  cleanPaymentParamsFromUrl(currentUrl);
                  currentUrl.searchParams.set("step", "0");
                  currentUrl.searchParams.delete("date");
                  currentUrl.searchParams.delete("proposalId");
                  currentUrl.searchParams.delete("proposed");
                  router.push(currentUrl.pathname + currentUrl.search);
                }}
              />
            ) : isCreditOnly ? (
              <div className="space-y-6">
                {paymentError && (
                  <PaymentErrorState errorMessage={paymentError} />
                )}
                <div className="rounded-xl border border-border bg-card p-6 text-center">
                  <h4 className="mb-2 text-lg font-semibold text-foreground">
                    Krediti pokrivaju pun iznos
                  </h4>
                  <p className="mb-6 text-sm text-muted-foreground">
                    Nećemo ti ništa naplatiti. Klikni na „Potvrdi rezervaciju“ i
                    sesija će biti zakazana.
                  </p>
                  <Button
                    size="lg"
                    onClick={async () => {
                      try {
                        await confirmCreditOnlyBooking.mutateAsync({
                          therapistId: sessionDetails.paymentInfo.therapistId,
                          therapyType: sessionDetails.therapyType,
                          sessionTimestamp:
                            sessionDetails.paymentInfo.sessionTimestamp,
                          sessionQuantity,
                          ...(sessionDetails.paymentInfo.proposalMessageId !=
                          null
                            ? {
                                proposalMessageId:
                                  sessionDetails.paymentInfo.proposalMessageId,
                              }
                            : {}),
                        });
                        utils.paymentManagement.getUsageForUser.invalidate();
                        utils.giftCard.getCreditBalance.invalidate();
                        const currentUrl = new URL(window.location.href);
                        currentUrl.searchParams.set("step", "3");
                        router.push(currentUrl.pathname + currentUrl.search);
                      } catch (err) {
                        const errorMessage =
                          err instanceof Error
                            ? err.message
                            : "Neuspešno potvrđivanje rezervacije";
                        setPaymentError(errorMessage);
                      }
                    }}
                    isLoading={confirmCreditOnlyBooking.isPending}
                    icon={CheckCircle}
                  >
                    Potvrdi rezervaciju
                  </Button>
                </div>
              </div>
            ) : clientSecret ? (
              <div className="space-y-6">
                {paymentError && (
                  <PaymentErrorState errorMessage={paymentError} />
                )}
                <StripePaymentElements
                  key={clientSecret}
                  clientSecret={clientSecret}
                  onSuccess={async () => {
                    utils.paymentManagement.getUsageForUser.invalidate();
                    utils.giftCard.getCreditBalance.invalidate();
                    router.push(returnUrl);
                  }}
                  onError={(error) => {
                    console.error("Payment error:", error);
                    setPaymentError(error);
                    pushGA4Event("payment_failed", {
                      currency: "EUR",
                      value: price,
                      items: [
                        {
                          item_id: sessionDetails.paymentInfo.therapistId,
                          item_name: sessionDetails.therapistName,
                          price,
                          quantity: 1,
                        },
                      ],
                    });
                    void callServerActionSafely(
                      "track_payment_failed",
                      () =>
                        trackEventAction(
                          {
                            event: "payment_failed",
                            data: {
                              value: price,
                              therapistId:
                                sessionDetails.paymentInfo.therapistId,
                              therapistName: sessionDetails.therapistName,
                              therapyType: sessionDetails.therapyType,
                              sourceUrl: window.location.pathname,
                            },
                          },
                          {
                            attributionBridgeId:
                              sessionDetails.paymentInfo.attributionBridgeId,
                            debugTestCode: getClientTrackingDebugCode(),
                          },
                        ),
                      {
                        therapistId: sessionDetails.paymentInfo.therapistId,
                        sourceUrl: window.location.pathname,
                      },
                      { failureReporting: "warning" },
                    );
                  }}
                  returnUrl={returnUrl}
                  paymentMetadata={paymentMetadata ?? undefined}
                  submitLabel={`Plati ${EUR.format(payableAmount)} i potvrdi termin`}
                />
              </div>
            ) : (
              <div className="flex min-h-[400px] items-center justify-center">
                <div className="w-full text-center md:max-w-md">
                  <div className="mx-auto mb-6 h-12 w-12 animate-spin rounded-full border-4 border-gray-200 border-t-brand lg:h-16 lg:w-16" />
                  <h4 className="mb-3 text-lg font-bold text-foreground lg:text-xl">
                    Učitavanje forme za plaćanje...
                  </h4>
                  <p className="text-base leading-relaxed text-muted-foreground lg:text-lg">
                    Sačekaj trenutak dok učitavamo sigurnu formu za plaćanje...
                  </p>
                </div>
              </div>
            )}
          </div>

          {!isOrgSponsored && (
            <div className="mt-5 border-t pt-4 text-sm leading-relaxed text-muted-foreground">
              <p>
                Ako otkažeš ili pomeriš termin više od 24 sata unapred, plaćena
                sesija ostaje na tvom nalogu za novi termin. Kasnije otkazivanje
                se tretira kao iskorišćena sesija.
              </p>
              <Link
                href="/podrska/klijenti/zakazivanje-i-termini"
                target="_blank"
                className="mt-2 inline-flex font-medium text-brand underline-offset-4 hover:underline focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand"
              >
                Pravila otkazivanja i pomeranja termina
              </Link>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
